import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AppLoader from './AppLoader';

interface Props {
  children: ReactNode;
}

/**
 * Estado de acceso, resuelto una sola vez para los tres guards:
 * - `loading`: sesión o /api/me todavía en vuelo
 * - `no-session`: no autenticado
 * - `no-org`: autenticado pero sin organización → onboarding
 * - `ready`: autenticado y con organización
 */
type AuthGate = 'loading' | 'no-session' | 'no-org' | 'ready';

function useAuthGate(): AuthGate {
  const { session, organization } = useAuth();

  if (session === undefined) return 'loading';
  if (session === null) return 'no-session';
  // undefined ≠ null: undefined es "aún cargando", null es "sin organización".
  // Colapsarlos mandaría al onboarding a usuarios que sí la tienen.
  if (organization === undefined) return 'loading';
  if (organization === null) return 'no-org';
  return 'ready';
}

export default function ProtectedRoute({ children }: Props) {
  const gate = useAuthGate();

  if (gate === 'loading') return <AppLoader />;
  if (gate === 'no-session') return <Navigate to="/login" replace />;
  // Ninguna pantalla funciona sin organización: el backend responde 403
  // ORG_REQUIRED en todas las rutas de datos.
  if (gate === 'no-org') return <Navigate to="/onboarding" replace />;

  return children;
}

export function OwnerRoute({ children }: Props) {
  const gate = useAuthGate();
  const { role } = useAuth();

  if (gate === 'loading') return <AppLoader />;
  if (gate === 'no-session') return <Navigate to="/login" replace />;
  if (gate === 'no-org') return <Navigate to="/onboarding" replace />;
  // Con la organización resuelta, role ya está cargado: null aquí significa
  // "sin rol", no "cargando" (antes ambos casos eran indistinguibles).
  if (role !== 'owner') return <Navigate to="/" replace />;

  return children;
}

/**
 * Inversa de ProtectedRoute para el onboarding: exige sesión, pero manda a `/`
 * si el usuario YA tiene organización (evita crear una segunda).
 */
export function OnboardingRoute({ children }: Props) {
  const gate = useAuthGate();

  if (gate === 'loading') return <AppLoader />;
  if (gate === 'no-session') return <Navigate to="/login" replace />;
  if (gate === 'ready') return <Navigate to="/" replace />;

  return children;
}
